/**
 * AwardMomentsStrip
 *
 * Cross-portfolio carousel of award-worthy post moments.
 * "Pitch ammunition at a glance" - surfaces the agency's best content
 * for awards, case studies, and client retainer reviews.
 *
 * Popover design: Dashboard-matched pattern.
 * Gradient header · full-width thumbnail · metric grid · summary bar · caption · CTAs.
 */

import { useState, useRef, useCallback, useEffect, type KeyboardEvent as ReactKeyboardEvent } from 'react';
import { useTranslation } from 'react-i18next';
import { lifecycleStageLabel } from '@/utils/portfolioLabels';
import { useTimezone } from '@/contexts/TimezoneContext';
import { ICONS } from '@/constants/icons';
import { SelectChevron } from '@/components/ui/Select';
import { Icon } from '@/components/ui/Icon';
import { Tooltip } from '@/components/ui/Tooltip';
import { HoverCard } from '@/components/ui/HoverCard';
import { BottomSheet } from '@/components/ui/BottomSheet';
import { useMediaQuery } from '@/hooks/useMediaQuery';
import { HorizontalCardCarousel } from '@/components/dashboard/HorizontalCardCarousel';
import { DashboardPostPopover } from '@/components/dashboard/DashboardPostPopover';
import { PlatformAvatars } from './PlatformAvatars';
import { type TopPost } from '@/data/topPosts.types';
import { usePortfolioHighlights } from '@/hooks/usePortfolio';
import type { PortfolioHighlightPost } from '@/services/portfolio.service';
import { useMutation } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import * as agencyService from '@/services/agency.service';
import { setAccessToken } from '@/services/api';
import { useAuth } from '@/hooks/useAuth';
import { cn } from '@/utils/cn';

// ── Constants ─────────────────────────────────────────────────────────────────

const STRIP_STORAGE_KEY = 'portfolio-award-strip-open';

// ── Lifecycle badge config ────────────────────────────────────────────────────

const LIFECYCLE_CONFIG: Record<string, { pill: string; label: string }> = {
  active:     { pill: 'bg-primary-100 text-primary-700',   label: 'Active' },
  onboarding: { pill: 'bg-peach/60 text-coral-700',     label: 'Onboarding' },
  churning:   { pill: 'bg-coral-100 text-coral-700',       label: 'Churning' },
  paused:     { pill: 'bg-gray-100 text-gray-600',         label: 'Paused' },
};

// ── Metric parsing ────────────────────────────────────────────────────────────

/**
 * Parse a metric string like "8.4k reach", "48k impressions", "+2.1k shares",
 * "ER 4.2%", "4× engagement", "940 saves" into { value, unit }.
 *
 * Rules:
 * - "ER 4.2%"  → value="4.2%", unit="ER"
 * - "8.4k reach" → value="8.4k", unit="REACH"
 * - "+2.1k shares" → value="+2.1k", unit="SHARES"
 * - "4× engagement" → value="4×", unit="ENGAGEMENT"
 * - "120k organic reach" → value="120k", unit="ORGANIC REACH"
 */
/** Format a raw number into a compact human-readable string: 12345 → "12.3k". */
function formatCompact(n: number): string {
  if (n >= 1_000_000) return `${(n / 1_000_000).toFixed(1)}M`;
  if (n >= 1_000) return `${(n / 1_000).toFixed(1)}k`;
  return String(n);
}

function parseMetric(metricStr: string): { value: string; unit: string } {
  const str = metricStr.trim();

  // Handle "ER 4.2%" format - unit prefix
  const erMatch = str.match(/^(ER)\s+(.+)$/i);
  if (erMatch) {
    return { value: erMatch[2], unit: 'ER' };
  }

  // Split on first space: first token = value, rest = unit
  const spaceIdx = str.indexOf(' ');
  if (spaceIdx === -1) {
    return { value: str, unit: '' };
  }

  const value = str.slice(0, spaceIdx);
  const unit = str.slice(spaceIdx + 1).toUpperCase();
  return { value, unit };
}

// ── Toast ─────────────────────────────────────────────────────────────────────

interface ToastState { visible: boolean; message: string }

// ── Award Popover - Dashboard-matched pattern ─────────────────────────────────

interface AwardPopoverProps {
  post: TopPost;
  formatRelativeDate: (date: string) => string;
  onCopyToPitchDeck: () => void;
  onOpenInCreator: () => void;
  t: (key: string, opts?: Record<string, unknown>) => string;
}

const AwardPopover = ({
  post,
  formatRelativeDate,
  onCopyToPitchDeck,
  onOpenInCreator,
  t,
}: AwardPopoverProps) => {
  // Backend always provides lifecycleStage; default to 'active' if missing.
  const lifecycle = LIFECYCLE_CONFIG[post.lifecycleStage ?? 'active'] ?? LIFECYCLE_CONFIG.active;
  const { value: metricValue, unit: metricUnit } = parseMetric(post.metric);

  return (
    <DashboardPostPopover
      className="w-[360px] lg:w-[380px] max-h-[min(80vh,640px)] flex flex-col"
      gradient="from-primary-500 to-primary-600"
      headerIcon={ICONS.STAR}
      headerLabel={t('award_strip_title', { defaultValue: 'Award-worthy' })}
      headerLines={[
        { icon: ICONS.CLOCK, text: formatRelativeDate(post.postedAt) },
      ]}
      postMedia={(post.media ?? []).map((m) => ({
        ...m,
        width: m.width ?? undefined,
        height: m.height ?? undefined,
        duration: m.duration ?? undefined,
      }))}
      caption={post.caption}
      // When the post has real media, fall through to the default CarouselThumb
      // (handles IMAGE / VIDEO / PDF). When media is missing we keep the
      // branded gradient placeholder + chip overlay for a graceful fallback.
      thumbnail={
        (post.media ?? []).length === 0 ? (
          <div
            className="aspect-square rounded-lg overflow-hidden relative"
            style={{ background: post.thumbnailPlaceholderGradient }}
            aria-hidden="true"
          >
            <div className="absolute top-2 left-2">
              <PlatformAvatars platforms={post.platforms} channelNames={post.channelNames} profilePhotoUrls={post.channelProfilePhotoUrls} size={20} />
            </div>
            <div className="absolute top-2 right-2 px-2 py-1 rounded-full bg-black/35 backdrop-blur-sm">
              <span className="text-[11px] font-semibold text-white leading-none">
                {metricValue}{metricUnit ? ` ${metricUnit}` : ''}
              </span>
            </div>
            {post.engagementRate !== undefined && (
              <div className="absolute bottom-2 left-2 flex items-center gap-1 px-2 py-1 rounded-full bg-primary-500/90">
                <i className="hm hm-star text-white" style={{ fontSize: 9 }} aria-hidden />
                <span className="text-[10px] font-semibold text-white leading-none">ER {post.engagementRate}%</span>
              </div>
            )}
          </div>
        ) : undefined
      }
      footer={
        <div className="flex gap-2">
          <button
            type="button"
            className="flex-1 flex items-center justify-center gap-1.5 px-3 py-2 rounded-lg border border-gray-200 text-xs font-medium text-gray-700 hover:bg-gray-50 transition-colors focus:outline-none focus-visible:border-primary-600"
            onClick={onOpenInCreator}
            aria-label={t('award_open_in_creator', { defaultValue: 'Open in Creator' })}
          >
            <i className="hm hm-pen-pencil" style={{ fontSize: 12 }} aria-hidden="true" />
            {t('award_open_in_creator', { defaultValue: 'Open in Creator' })}
          </button>
          <button
            type="button"
            className="flex-1 flex items-center justify-center gap-1.5 px-3 py-2 rounded-lg bg-primary-500 text-primary-contrast text-xs font-semibold hover:bg-primary-600 transition-colors focus:outline-none border border-transparent focus-visible:border-gray-900"
            onClick={onCopyToPitchDeck}
            aria-label={t('award_use_in_pitch', { defaultValue: 'Use in pitch deck' })}
          >
            <i className="hm hm-copy" style={{ fontSize: 12 }} aria-hidden="true" />
            {t('award_use_in_pitch', { defaultValue: 'Use in pitch deck' })}
          </button>
        </div>
      }
    >
      {/* Real analytics for the winning version of this post */}
      <div className="mt-3 pt-3 border-t border-gray-100">
        <div className="grid grid-cols-2 gap-2">
          {(post.totalEngagements ?? 0) > 0 && (
            <div className="text-center p-2 bg-gray-50 rounded-lg">
              <div className="text-xs text-gray-500 mb-0.5">
                {t('award_engagements_label', { defaultValue: 'Engagements' })}
              </div>
              <div className="text-sm font-bold text-gray-900">
                {formatCompact(post.totalEngagements ?? 0)}
              </div>
            </div>
          )}
          {(post.totalReach ?? 0) > 0 && (
            <div className="text-center p-2 bg-gray-50 rounded-lg">
              <div className="text-xs text-gray-500 mb-0.5">
                {t('award_reach_label', { defaultValue: 'Reach' })}
              </div>
              <div className="text-sm font-bold text-gray-900">
                {formatCompact(post.totalReach ?? 0)}
              </div>
            </div>
          )}
          {(post.totalImpressions ?? 0) > 0 && (
            <div className="text-center p-2 bg-gray-50 rounded-lg">
              <div className="text-xs text-gray-500 mb-0.5">
                {t('award_impressions_label', { defaultValue: 'Impressions' })}
              </div>
              <div className="text-sm font-bold text-gray-900">
                {formatCompact(post.totalImpressions ?? 0)}
              </div>
            </div>
          )}
          {post.engagementRate !== undefined && (
            <div className="text-center p-2 bg-gray-50 rounded-lg">
              <div className="text-xs text-gray-500 mb-0.5">
                {t('award_er_label', { defaultValue: 'Engagement Rate' })}
              </div>
              <div className="text-sm font-bold text-gray-900">{post.engagementRate}%</div>
            </div>
          )}
        </div>
      </div>
      {/* Client row */}
      <div className="mt-3 pt-3 border-t border-gray-100">
        <p className="text-xs font-medium text-gray-500 uppercase tracking-wide mb-2">
          {t('award_client_label', { defaultValue: 'Client' })}
        </p>
        <div className="flex items-center gap-2">
          {post.clientLogoUrl ? (
            <img src={post.clientLogoUrl} alt="" className="w-6 h-6 rounded-full ring-2 ring-white/40 object-cover shrink-0" />
          ) : (
            <div
              className={cn(
                'w-6 h-6 rounded-md flex items-center justify-center font-bold text-white shrink-0 text-[11px]',
                post.clientAvatarColor
              )}
              aria-hidden
            >
              {post.clientName.charAt(0)}
            </div>
          )}
          <span className="text-sm font-semibold text-gray-900 flex-1 truncate">{post.clientName}</span>
          <span className={cn('text-[10px] font-medium px-2 py-0.5 rounded-full shrink-0', lifecycle.pill)}>
            {lifecycleStageLabel(t, post.lifecycleStage ?? 'active')}
          </span>
        </div>
      </div>
    </DashboardPostPopover>
  );
};

// ── Post Card (160px - matches Dashboard pattern exactly) ─────────────────────

interface PostCardProps {
  post: TopPost;
  onCopy: (post: TopPost) => void;
  formatRelativeDate: (date: string) => string;
  t: (key: string, opts?: Record<string, unknown>) => string;
}

const PostCard = ({ post, onCopy, formatRelativeDate, t }: PostCardProps) => {
  const navigate = useNavigate();
  const { refreshUser } = useAuth();
  // Radix HoverCard content is deliberately not keyboard-reachable (its tabbables
  // get tabindex=-1). Desktop keyboard users instead activate the focusable card
  // (Enter/Space) to open the same content in a focus-trapped sheet.
  const isMobile = useMediaQuery('(max-width: 1023px)');
  const [hoverOpen, setHoverOpen] = useState(false);
  const [keyboardOpen, setKeyboardOpen] = useState(false);
  // Radix HoverCard also opens on trigger focus. Only honour opens while the pointer
  // is over the card, so tabbing to it (or focus returning after the sheet closes)
  // never pops the unreachable hover popover.
  const pointerInsideRef = useRef(false);

  // Crossing the desktop/mobile breakpoint swaps the interaction model; drop stale state.
  useEffect(() => {
    setKeyboardOpen(false);
    setHoverOpen(false);
  }, [isMobile]);

  const handleHoverOpenChange = useCallback((next: boolean) => {
    // Desktop only: on mobile HoverCard hands this to BottomSheet (tap-to-open, no pointer hover).
    if (!isMobile && next && !pointerInsideRef.current) return;
    setHoverOpen(next);
  }, [isMobile]);

  const cardRef = useRef<HTMLDivElement>(null);
  const closedTimerRef = useRef<number | null>(null);
  useEffect(
    () => () => {
      if (closedTimerRef.current !== null) window.clearTimeout(closedTimerRef.current);
    },
    []
  );
  // BottomSheet unmounts ~300ms after close and Radix then restores focus to a
  // (nonexistent) trigger, i.e. body. Take over and return focus to the card.
  const handleSheetCloseAutoFocus = useCallback((e: Event) => {
    e.preventDefault();
    cardRef.current?.focus({ preventScroll: true });
  }, []);
  // Belt and braces for the Escape path: once the sheet is really gone, make sure
  // focus is on the card (Radix's own restore can land on body after ours).
  const handleSheetClosed = useCallback(() => {
    if (closedTimerRef.current !== null) window.clearTimeout(closedTimerRef.current);
    closedTimerRef.current = window.setTimeout(() => {
      closedTimerRef.current = null;
      if (!cardRef.current) return;
      const active = document.activeElement;
      if (!active || active === document.body) cardRef.current.focus({ preventScroll: true });
    }, 50);
  }, []);

  const handleCopy = useCallback(() => {
    const pitchText = `${post.clientName} - ${post.metric}\n${post.reason}\n\n"${post.caption}"`;
    void navigator.clipboard.writeText(pitchText).then(() => onCopy(post));
  }, [post, onCopy]);

  // Enter God Mode into the post's client workspace, then open the post in creator
  const enterMutation = useMutation({
    mutationKey: ['enter-god-mode'],
    mutationFn: (teamId: number) => agencyService.enterGodMode(teamId),
  });

  const handleOpenInCreator = useCallback(async () => {
    const teamId = parseInt(post.clientId, 10);
    if (!Number.isFinite(teamId)) return;
    try {
      const data = await enterMutation.mutateAsync(teamId);
      setAccessToken(data.accessToken);
      await refreshUser();
      navigate(`/creator?post=${post.id}`);
    } catch {
      // silently fail - tooltip already communicates expectation
    }
  }, [enterMutation, navigate, post.clientId, post.id, refreshUser]);

  // 160×160 card trigger - matches Dashboard w-[160px] aspect-square pattern exactly
  const cardThumb = post.media?.[0]?.thumbnailUrl ?? post.media?.[0]?.originalUrl ?? null;
  const cardTrigger = (
    <div
      ref={cardRef}
      className="group flex-none w-[160px] aspect-square rounded-xl overflow-hidden relative cursor-pointer focus:outline-none hover:shadow-lg hover:scale-[1.03] transition-all duration-200"
      style={cardThumb ? undefined : { background: post.thumbnailPlaceholderGradient }}
      aria-label={`${post.clientName}: ${post.metric}`}
      onPointerEnter={() => { pointerInsideRef.current = true; }}
      onPointerDown={() => { pointerInsideRef.current = true; }}
      onPointerLeave={() => { pointerInsideRef.current = false; }}
      {...(isMobile
        ? {}
        : {
            role: 'button',
            tabIndex: 0,
            'aria-haspopup': 'dialog' as const,
            onKeyDown: (e: ReactKeyboardEvent<HTMLDivElement>) => {
              // Only the card itself opens the sheet, not keys bubbling from inner controls.
              if (e.target !== e.currentTarget) return;
              if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault();
                setHoverOpen(false);
                setKeyboardOpen(true);
              }
            },
          })}
    >
      {cardThumb && (
        <img
          src={cardThumb}
          alt=""
          loading="lazy"
          className="absolute inset-0 w-full h-full object-cover"
        />
      )}
      {/* Focus indicator: border-colour shift as an overlay so layout and thumbnail don't move */}
      <div
        className="pointer-events-none absolute inset-0 z-10 rounded-xl border-2 border-transparent group-focus-visible:border-gray-900"
        aria-hidden="true"
      />
      {/* Top overlay - stacked platform avatars (one circle per platform) */}
      <div className="absolute top-0 inset-x-0 p-2 flex items-center gap-1">
        <PlatformAvatars platforms={post.platforms} channelNames={post.channelNames} profilePhotoUrls={post.channelProfilePhotoUrls} size={20} />
        {/* Client logo or initial - top-right */}
        <div className="ml-auto">
          {post.clientLogoUrl ? (
            <img src={post.clientLogoUrl} alt="" className="w-6 h-6 rounded-full ring-2 ring-white/40 object-cover" aria-hidden="true" />
          ) : (
            <div
              className={cn(
                'w-6 h-6 rounded-full flex items-center justify-center text-[10px] font-bold text-white ring-2 ring-white/40',
                post.clientAvatarColor
              )}
              aria-hidden="true"
            >
              {post.clientName.charAt(0)}
            </div>
          )}
        </div>
      </div>

      {/* Bottom gradient overlay - 3 rows matching ReviewRemixSection exactly */}
      <div className="absolute inset-x-0 bottom-0 bg-gradient-to-t from-black/90 via-black/60 to-transparent p-2 pt-8">
        {/* Row 1: metric + star icon */}
        <div className="flex items-center gap-1 mb-0.5">
          <i className="hm hm-star text-white" style={{ fontSize: 10 }} aria-hidden="true" />
          <span className="text-[11px] text-white font-medium drop-shadow-sm">
            {post.metric}
          </span>
        </div>
        {/* Row 2: posted date */}
        <div className="flex items-center gap-1 mb-0.5">
          <Icon name={ICONS.CLOCK} size="xs" className="text-primary-300" ariaHidden />
          <span className="text-[10px] text-white/80 drop-shadow-sm">
            {formatRelativeDate(post.postedAt)}
          </span>
        </div>
        {/* Row 3: caption (1-line clamp) */}
        <p className="text-[11px] text-white/90 line-clamp-1 drop-shadow-sm italic">
          {post.caption.length > 40
            ? `${post.caption.slice(0, 40)}…`
            : post.caption}
        </p>
      </div>
    </div>
  );

  return (
    <>
      <HoverCard
        trigger={cardTrigger}
        side="right"
        align="start"
        className="w-[360px] lg:w-[380px]"
        open={hoverOpen && !keyboardOpen}
        onOpenChange={handleHoverOpenChange}
      >
        <AwardPopover
          post={post}
          formatRelativeDate={formatRelativeDate}
          onCopyToPitchDeck={handleCopy}
          onOpenInCreator={handleOpenInCreator}
          t={t}
        />
      </HoverCard>
      {!isMobile && (
        <BottomSheet
          open={keyboardOpen}
          onOpenChange={setKeyboardOpen}
          onCloseAutoFocus={handleSheetCloseAutoFocus}
          onClosed={handleSheetClosed}
          className="max-w-[420px] mx-auto"
        >
          <AwardPopover
            post={post}
            formatRelativeDate={formatRelativeDate}
            onCopyToPitchDeck={handleCopy}
            onOpenInCreator={handleOpenInCreator}
            t={t}
          />
        </BottomSheet>
      )}
    </>
  );
};


// ── Main Component ────────────────────────────────────────────────────────────

/** Map backend PortfolioHighlightPost → frontend TopPost shape */
function mapHighlightToTopPost(p: PortfolioHighlightPost, untitled: string): TopPost {
  return {
    id: p.id,
    clientId: p.clientId,
    clientName: p.clientName,
    clientAvatarColor: p.clientAvatarColor,
    caption: p.caption?.trim() ? p.caption : untitled,
    thumbnailPlaceholderGradient: p.thumbnailPlaceholderGradient,
    platforms:
      p.platforms && p.platforms.length > 0
        ? (p.platforms as TopPost['platforms'])
        : ['LINKEDIN'],
    channelNames: p.channelNames,
    channelProfilePhotoUrls: p.channelProfilePhotoUrls,
    lifecycleStage: p.lifecycleStage,
    totalImpressions: p.totalImpressions,
    totalReach: p.totalReach,
    totalEngagements: p.totalEngagements,
    metric: p.metric,
    metricValue: p.metricValue,
    reason: p.reason,
    postedAt: p.postedAt,
    engagementRate: p.engagementRate,
    media: p.media,
    clientLogoUrl: p.clientLogoUrl,
  };
}

export type AwardPeriod = 'today' | 'week' | 'month' | 'quarter';

interface AwardMomentsStripProps {
  /** Selected look-back window for the Award strip. */
  period: AwardPeriod;
  /** Called when the user picks a different period from the inline pills. */
  onPeriodChange: (period: AwardPeriod) => void;
}

const PERIOD_OPTIONS: { value: AwardPeriod; labelKey: string; shortKey: string; defaultLabel: string; shortLabel: string }[] = [
  { value: 'today', labelKey: 'period_today', shortKey: 'agency:award_period_short_today', defaultLabel: 'Today', shortLabel: 'Today' },
  { value: 'week', labelKey: 'period_this_week', shortKey: 'agency:award_period_short_week', defaultLabel: 'This Week', shortLabel: 'Week' },
  { value: 'month', labelKey: 'period_this_month', shortKey: 'agency:award_period_short_month', defaultLabel: 'This Month', shortLabel: 'Month' },
  { value: 'quarter', labelKey: 'agency:period_this_quarter', shortKey: 'agency:award_period_short_quarter', defaultLabel: 'This Quarter', shortLabel: 'Quarter' },
];

const AwardPeriodPills = ({
  value,
  onChange,
}: {
  value: AwardPeriod;
  onChange: (p: AwardPeriod) => void;
}) => {
  const { t } = useTranslation('common');
  return (
    <div
      className="grid w-full grid-cols-4 gap-1 sm:inline-flex sm:w-auto sm:items-center sm:gap-1.5"
      role="group"
      aria-label={t('period_filter', { defaultValue: 'Period filter' })}
    >
      {PERIOD_OPTIONS.map((opt) => {
        const isActive = value === opt.value;
        return (
          <button
            key={opt.value}
            type="button"
            onClick={(e) => {
              e.stopPropagation();
              onChange(opt.value);
            }}
            aria-pressed={isActive}
            aria-label={t(opt.labelKey, { defaultValue: opt.defaultLabel })}
            className={`min-w-0 w-full sm:w-auto px-1 sm:px-2.5 h-11 sm:h-6 inline-flex items-center justify-center rounded-md text-[10px] sm:text-[11px] whitespace-nowrap font-medium transition-all duration-150 outline-none focus-visible:border-primary-600 ${
              isActive
                ? 'bg-gray-900 text-white border border-gray-900'
                : 'border border-gray-200 text-gray-600 bg-white hover:bg-gray-50 hover:border-gray-300'
            }`}
          >
            <span className="sm:hidden">{t(opt.shortKey, { defaultValue: opt.shortLabel })}</span>
            <span className="hidden sm:inline">{t(opt.labelKey, { defaultValue: opt.defaultLabel })}</span>
          </button>
        );
      })}
    </div>
  );
};

export const AwardMomentsStrip = ({ period, onPeriodChange }: AwardMomentsStripProps) => {
  const { t } = useTranslation('agency');
  const { formatRelativeDate } = useTimezone();
  const { data, isLoading } = usePortfolioHighlights(period);
  const topPosts: TopPost[] = (data?.topPosts ?? []).map((p) =>
    mapHighlightToTopPost(p, t('common:untitled_post'))
  );

  // Visibility probe: only render the strip at all when the widest window
  // (quarter) has at least one award-worthy post. Otherwise the agency simply
  // has no awards anywhere yet — hiding is cleaner than an empty section.
  // Skip this probe when the user is already looking at quarter (would be redundant).
  const { data: quarterData } = usePortfolioHighlights('quarter');
  const hasAnyAwards = (quarterData?.topPosts?.length ?? 0) > 0;

  // Collapsible state - persisted in localStorage; default collapsed
  const [isOpen, setIsOpen] = useState<boolean>(() => {
    try {
      const stored = localStorage.getItem(STRIP_STORAGE_KEY);
      return stored !== null ? stored === 'true' : false;
    } catch {
      return false;
    }
  });

  const [toast, setToast] = useState<ToastState>({ visible: false, message: '' });
  const toastTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const showToast = useCallback((message: string) => {
    setToast({ visible: true, message });
    if (toastTimeoutRef.current) clearTimeout(toastTimeoutRef.current);
    toastTimeoutRef.current = setTimeout(() => {
      setToast({ visible: false, message: '' });
    }, 2800);
  }, []);

  useEffect(() => {
    return () => {
      if (toastTimeoutRef.current) clearTimeout(toastTimeoutRef.current);
    };
  }, []);

  const handleCopy = useCallback(
    (post: TopPost) => {
      const pitchText = `${post.clientName} - ${post.metric}\n${post.reason}\n\n"${post.caption}"`;
      void navigator.clipboard.writeText(pitchText).then(() => {
        showToast(t('award_copied_to_clipboard', { defaultValue: 'Copied to clipboard' }));
      });
    },
    [showToast, t]
  );

  const toggleOpen = () => {
    const next = !isOpen;
    setIsOpen(next);
    try {
      localStorage.setItem(STRIP_STORAGE_KEY, String(next));
    } catch {
      // localStorage may be unavailable
    }
  };

  // No award-worthy posts in any window we'd offer → hide the strip entirely.
  // (Still loading? Don't flash an empty state; render nothing until probe lands.)
  if (!isLoading && !hasAnyAwards) return null;

  return (
    <div className="relative z-10 bg-white border-b border-gray-900/10 portfolio-section">
      {/* ── Strip Header - editorial overline + period pills + hairline ────── */}
      <div className="flex flex-wrap items-center gap-x-3 gap-y-2 px-4 sm:px-6 py-3">
        {/* Overline title + collapsed thumbnail preview */}
        <div className="flex items-center gap-2 flex-1 min-w-0">
          <Tooltip
            content={t('award_strip_tooltip', {
              defaultValue:
                'Auto-surfaced top posts across your portfolio. Use as pitch ammunition.',
            })}
            side="bottom"
            delayDuration={300}
          >
            <h2 className="text-[11px] font-medium uppercase tracking-[0.2em] text-gray-500 leading-tight whitespace-nowrap cursor-default">
              {t('award_strip_title', { defaultValue: 'Award-worthy' })}
            </h2>
          </Tooltip>
          {!isOpen && topPosts.length > 0 && (
            <button
              type="button"
              onClick={toggleOpen}
              className="flex items-center -space-x-1.5 isolate ml-1 rounded-full hover:opacity-80 transition-opacity focus:outline-none border border-transparent focus-visible:border-primary-600"
              aria-label={t('award_expand_with_count', {
                defaultValue: 'Show {{count}} award moments',
                count: topPosts.length,
              })}
            >
              {topPosts.slice(0, 5).map((p) => {
                const thumb = p.media?.[0]?.thumbnailUrl ?? p.media?.[0]?.originalUrl ?? null;
                return (
                  <div
                    key={p.id}
                    className="w-5 h-5 rounded-full ring-2 ring-white overflow-hidden flex-shrink-0"
                    style={thumb ? undefined : { background: p.thumbnailPlaceholderGradient }}
                    aria-hidden="true"
                  >
                    {thumb && (
                      <img
                        src={thumb}
                        alt=""
                        loading="lazy"
                        className="w-full h-full object-cover"
                      />
                    )}
                  </div>
                );
              })}
              {topPosts.length > 5 && (
                <div
                  className="w-5 h-5 rounded-full ring-2 ring-white bg-gray-100 flex items-center justify-center text-[8px] font-semibold text-gray-600 flex-shrink-0"
                  aria-hidden="true"
                >
                  +{topPosts.length - 5}
                </div>
              )}
            </button>
          )}
        </div>

        {/* On phones the four periods need their own row; shrinking them beside
            the title made the labels wrap over each other. */}
        <div className="order-3 w-full sm:order-none sm:w-auto sm:shrink-0">
          <AwardPeriodPills value={period} onChange={onPeriodChange} />
        </div>

        {/* See all link */}
        {isOpen && (
          <button
            type="button"
            className="text-xs font-medium text-primary-600 hover:text-primary-700 transition-colors shrink-0 focus:outline-none border border-transparent focus-visible:border-primary-600 rounded px-1"
            aria-label={t('award_see_all', { defaultValue: 'See all award moments' })}
          >
            {t('award_see_all', { defaultValue: 'See all' })}
          </button>
        )}

        {/* Collapse toggle */}
        <button
          type="button"
          onClick={toggleOpen}
          className="flex items-center gap-1 text-xs text-gray-400 hover:text-gray-600 transition-colors shrink-0 focus:outline-none border border-transparent focus-visible:border-primary-600 rounded px-1 py-0.5"
          aria-expanded={isOpen}
          aria-label={
            isOpen
              ? t('award_collapse', { defaultValue: 'Collapse award moments' })
              : t('award_expand', { defaultValue: 'Show award moments' })
          }
        >
          <SelectChevron className={`shrink-0 transition-transform ${isOpen ? '' : '-rotate-90'}`} />
          <span className="hidden sm:inline">
            {isOpen
              ? t('award_collapse', { defaultValue: 'Collapse' })
              : t('award_expand', { defaultValue: 'Show' })}
          </span>
        </button>
      </div>

      {/* ── Carousel ─────────────────────────────────────────────────────── */}
      {isOpen && (
        <div
          className="px-4 sm:px-6 pb-4 pt-1"
          role="list"
          aria-label={t('award_strip_title', { defaultValue: 'Award-worthy' })}
        >
          {topPosts.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-10 text-center">
              <div className="w-12 h-12 bg-gray-100 rounded-full grid place-items-center mb-3">
                <i className="hm hm-star text-gray-400" style={{ fontSize: 20 }} aria-hidden="true" />
              </div>
              <p className="text-gray-500 text-sm">
                {t('award_strip_empty', {
                  defaultValue: 'No award-worthy posts in this period. Try a wider window.',
                })}
              </p>
            </div>
          ) : (
            <HorizontalCardCarousel>
              {topPosts.map((post) => (
                <div key={post.id} role="listitem">
                  <PostCard
                    post={post}
                    onCopy={handleCopy}
                    formatRelativeDate={formatRelativeDate}
                    t={t}
                  />
                </div>
              ))}
            </HorizontalCardCarousel>
          )}
        </div>
      )}

      {/* ── Toast ─────────────────────────────────────────────────────────── */}
      {toast.visible && (
        <div
          role="status"
          aria-live="polite"
          className="fixed bottom-[calc(var(--bottom-nav-h)+env(safe-area-inset-bottom)+12px+var(--floating-stack-offset))] lg:bottom-6 left-1/2 -translate-x-1/2 z-50 flex items-center gap-2 px-4 py-2.5 bg-gray-900 text-white text-sm font-medium rounded-xl shadow-xl"
        >
          <i
            className="hm hm-tick-circle text-primary-400"
            style={{ fontSize: 15 }}
            aria-hidden="true"
          />
          {toast.message}
        </div>
      )}
    </div>
  );
};

export default AwardMomentsStrip;
